import type { ValuesType } from 'utility-types'
import React, { type FC, type ReactNode } from 'react'

import { isArray, objectEntries } from '../../../../utils'

import { Capability, type CybusDetailedCoreContainer, type CybusDetailedServiceContainer, type CybusLogSource, Translation } from '../../../../domain'

import { Hyphen, Table } from '../../common'
import { FormattedList, FormattedTranslation } from '../../Internationalization'
import Logs, { LogsAvailability } from '../../Logs'
import { Tab, Tabs } from '../../Tabs'

type ConfigValue = ValuesType<CybusDetailedServiceContainer['config']>

const renderConfigValue = (value: ConfigValue): ReactNode => {
    if (typeof value === 'boolean') {
        return <FormattedTranslation id={Translation.BOOLEAN} values={{ value }} />
    }

    if (isArray(value)) {
        return value.length > 0 ? <FormattedList value={value} /> : <Hyphen />
    }

    return value === null || value === '' ? <Hyphen /> : value
}

export const Content: FC<Readonly<{ logType: CybusLogSource, resource: CybusDetailedServiceContainer | CybusDetailedCoreContainer }>> = ({
    logType,
    resource,
}) => {
    const { id, variables, config } = resource

    return (
        <LogsAvailability type={logType} id={id}>
            {(available) => (
                <Tabs>
                    <Tab title={Translation.CONTAINER_LOGS} disabled={!available} requiredCapabilities={[Capability.LOGS_READ]}>
                        <Logs id={id} type={logType} showDownloadRawLogs reloadOn={resource} />
                    </Tab>
                    <Tab title={Translation.CONFIGURATION}>
                        <Table
                            data={objectEntries(config).map(([name, value]) => ({ name, value }))}
                            columns={{
                                name: { label: Translation.NAME, sort: true },
                                value: { label: Translation.VALUE, customCellRender: renderConfigValue },
                            }}
                        />
                    </Tab>
                    <Tab title={Translation.ENVIRONMENT_VARIABLE}>
                        <Table
                            data={objectEntries(variables).map(([name, value]) => ({ name, value }))}
                            columns={{ name: { label: Translation.NAME, sort: true }, value: { label: Translation.VALUE } }}
                        />
                    </Tab>
                </Tabs>
            )}
        </LogsAvailability>
    )
}
